import {DataShape} from "../App";
import {ResponsiveLine} from "@nivo/line";
import React from "react";
import {area, curveMonotoneX} from "d3-shape";
import {Defs} from "@nivo/core";

const Vaccination = ({data}: { data: DataShape }) => {

    /**
     * Given a current rt, and a target rt (usually 1), and a current vaccination rate and efficacy
     * against infection, calculate what future vaccination rate would move the current rt to rTarget.
     *
     * CDC says that mRNA vaccines are between 26% and 84% effective (95% certainty, 66% estimated)
     * against infection, and there is an additional 40%-60% protection against vaccinated people
     * passing the virus to others, which yields a range of 55.6 - 93.6 effectiveness.
     *
     * To get a single number, an estimate of 66% efficacy and 50% resistance would work, or 83%
     * overall.
     *
     * @param rt
     * @param vacRate
     * @param eff
     * @param rTarget
     */
    const targetPercent = (rt: number, vacRate: number, eff: number, rTarget: number): number => {
        return (1 - (rTarget * (
                (1 - (vacRate * eff)) /
                rt))
        ) / eff
    }

    const vaccineData = [{
        id: `Vaccine #1`,
        data: data.metricsTimeseries.filter(el => el.vaccinationsInitiatedRatio !== null && el.vaccinationsInitiatedRatio !== undefined).map(el => {
            return {
                x: el.date,
                y: el.vaccinationsInitiatedRatio
            }
        })
    }, {
        id: `Vaccine #2`,
        data: data.metricsTimeseries.filter(el => el.vaccinationsCompletedRatio !== null && el.vaccinationsCompletedRatio !== undefined).map(el => {
            const vaccines = el.vaccinationsCompletedRatio as number;
            const targetLower: number = el.infectionRate ? targetPercent(el.infectionRate, vaccines, 0.936, 1) : vaccines;
            const targetUpper: number = el.infectionRate ? targetPercent(el.infectionRate, vaccines, 0.556, 1) : vaccines;
            return {
                x: el.date,
                rt: el.infectionRate,
                y: el.vaccinationsCompletedRatio,
                lower: Math.max(targetLower, vaccines),
                upper: Math.max(targetUpper, vaccines)
            }
        })
    }, {
        id: `Vaccine #3`,
        data: data.metricsTimeseries.filter(el => el.vaccinationsAdditionalDoseRatio !== null && el.vaccinationsAdditionalDoseRatio !== undefined).map(el => {
            return {
                x: el.date,
                y: el.vaccinationsAdditionalDoseRatio
            }
        })
    }]


    const AreaLayer = ({series, xScale, yScale}: any) => {
        const areaGenerator = area()
            .x((d: [any, any]) => {
                return xScale(d['data' as any].x)
            })
            .y0((d: [any, any]) => {
                const data = d['data' as any];
                return yScale(data.lower);
            })
            .y1((d: [any, any]) => {
                const data = d['data' as any];
                return yScale(data.upper);
            })
            .curve(curveMonotoneX)

        return (
            <>
                <Defs
                    defs={[
                        {
                            id: 'pattern',
                            type: 'patternLines',
                            background: 'transparent',
                            color: '#fc9926',
                            lineWidth: 1,
                            spacing: 5,
                            rotation: -45,
                        },
                    ]}
                />
                <path
                    d={areaGenerator(series[1].data) || undefined}
                    fill="url(#pattern)"
                    fillOpacity={0.6}
                    stroke="#fc9926"
                    strokeWidth={1.5}
                />
            </>
        )
    }

    return (
        <ResponsiveLine
            data={vaccineData}
            margin={{top: 10, right: 140, bottom: 50, left: 60}}
            colors={{"scheme": "category10"}}
            xScale={{
                format: "%Y-%m-%d",
                type: "time",
                useUTC: false,
            }}
            xFormat="time:%Y-%m-%d"
            yScale={{type: 'linear', min: 'auto', max: 1, stacked: false, reverse: false}}
            // yFormat=" >-.2f"
            yFormat=" >-.1%"
            curve="cardinal"
            axisTop={null}
            axisRight={null}
            axisBottom={{
                // tickValues: "every 3 month",
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                format: "%Y-%m",
                legend: "Date",
                legendOffset: 36,
                legendPosition: "middle"
            }}
            axisLeft={{
                // orient: 'left',
                format: ">-.0%",
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legend: 'Vaccination %',
                legendOffset: -45,
                legendPosition: 'middle'
            }}
            enablePoints={false}
            pointSize={10}
            pointColor={{theme: 'background'}}
            pointBorderWidth={2}
            pointBorderColor={{from: 'serieColor'}}
            pointLabelYOffset={-12}
            layers={[
                'grid',
                'markers',
                'areas',
                AreaLayer,
                'lines',
                'slices',
                'axes',
                'points',
                'legends',
            ]}
            isInteractive={true}
            // useMesh={true}
            enableSlices={"x"}
            sliceTooltip={({slice}) => {
                return (
                    <div
                        style={{
                            background: 'white',
                            padding: '9px 12px',
                            border: '1px solid #ccc',
                        }}
                    >
                        <div>{slice.points[0].data.xFormatted}</div>
                        {slice.points.map(point => (
                            <div
                                key={point.id}
                                style={{
                                    color: point.serieColor,
                                    padding: '3px 0',
                                }}
                            >
                                <strong>{point.serieId}</strong> {point.data.yFormatted}
                            </div>
                        ))}
                    </div>
                )
            }}
            legends={[
                {
                    anchor: 'bottom-right',
                    direction: 'column',
                    justify: false,
                    translateX: 100,
                    translateY: 0,
                    itemsSpacing: 0,
                    itemDirection: 'left-to-right',
                    itemWidth: 80,
                    itemHeight: 20,
                    itemOpacity: 0.75,
                    symbolSize: 12,
                    symbolShape: 'circle',
                    symbolBorderColor: 'rgba(0, 0, 0, .5)',
                    effects: [
                        {
                            on: 'hover',
                            style: {
                                itemBackground: 'rgba(0, 0, 0, .03)',
                                itemOpacity: 1
                            }
                        }
                    ]
                }
            ]}
        />
    );
}

export default Vaccination;